import { Routes } from '@angular/router';
import { authGuard } from './services/authguard/authguard.guard';
import { submoduleGuard } from './services/authguard/submodule.guard';
import { rightsLoadedGuard } from './services/authguard/rights-loaded.guard';
import { mandatoryAlertsGuard } from './services/authguard/mandatory-alerts.guard';
import { terminatedRestrictionGuard } from './services/authguard/terminated-restriction.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./modules/auth/auth.component').then((m) => m.AuthComponent),
    loadChildren: () =>
      import('./modules/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./modules/auth/auth.component').then((m) => m.AuthComponent),
    loadChildren: () =>
      import('./modules/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: '',
    loadComponent: () =>
      import('./modules/summary-main/summary-main.component').then(
        (m) => m.SummaryMainComponent
      ),
    // EN-29808 - the shell holds the side menu, which reads the employee's access modules once and
    // synchronously. On a reload those are still empty, so the menu rendered without its items and
    // asked for the wrong profile endpoint. This waits for them; it authorises nothing.
    canActivate: [rightsLoadedGuard],
    // EN-32926 - a page opened directly goes through Summary's Mandatory alert check first.
    canActivateChild: [mandatoryAlertsGuard],
    children: [
      {
        path: 'summary',
        loadComponent: () =>
          import('./modules/summary/summary.component').then(
            (m) => m.SummaryComponent
          ),
        loadChildren: () =>
          import('./modules/summary/summary.routes').then(
            (m) => m.SUMMARY_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Summary'],
          redirectTo: 'documents',
        },
      },
      {
        path: 'documents',
        loadComponent: () =>
          import('./modules/documents/documents.component').then(
            (m) => m.DocumentsComponent
          ),
        loadChildren: () =>
          import('./modules/documents/documents.routes').then(
            (m) => m.DOCUMENTS_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Documents'],
          redirectTo: 'paystub',
        },
      },
      {
        path: 'paystub',
        loadComponent: () =>
          import('./modules/paystub/paystub.component').then(
            (m) => m.PaystubComponent
          ),
        loadChildren: () =>
          import('./modules/paystub/paystub.routes').then(
            (m) => m.PAYSTUB_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Paystub'],
          redirectTo: 'pto',
        },
      },
      {
        path: 'pto',
        loadComponent: () =>
          import('./modules/paid-time-off/paid-time-off.component').then(
            (m) => m.PaidTimeOffComponent
          ),
        loadChildren: () =>
          import('./modules/paid-time-off/paid-time-off.routes').then(
            (m) => m.PTO_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Paid Time Off'],
          redirectTo: 'pto',
        },
      },
      {
        path: 'bank',
        loadComponent: () =>
          import('./modules/bank-deposit-info/bank-deposit-info.component').then(
            (m) => m.BankDepositInfoComponent
          ),
        loadChildren: () =>
          import('./modules/bank-deposit-info/bank-deposit-info.routes').then(
            (m) => m.BANK_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Bank Deposit Info'],
          redirectTo: 'tax',
        },
      },
      {
        path: 'tax',
        loadComponent: () =>
          import('./modules/tax-main/tax-main.component').then(
            (m) => m.TaxMainComponent
          ),
        loadChildren: () =>
          import('./modules/tax-main/tax-main.routes').then(
            (m) => m.TAX_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Tax'],
          redirectTo: 'tasks',
        },
      },
      {
        path: 'tasks',
        loadComponent: () =>
          import('./modules/task/task.component').then((m) => m.TaskComponent),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Task'],
          redirectTo: 'messages',
        },
      },
      {
        path: 'messages',
        loadComponent: () =>
          import('./modules/messages/messages.component').then(
            (m) => m.MessagesComponent
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Messages'],
          redirectTo: 'notes',
        },
      },
      {
        path: 'notes',
        loadComponent: () =>
          import('./modules/notes/notes.component').then(
            (m) => m.NotesComponent
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Notes'],
          redirectTo: 'benefits',
        },
      },
      {
        path: 'time-and-attendance',
        loadComponent: () =>
          import('./modules/time-and-attendance/time-and-attendance.component').then(
            (m) => m.TimeAndAttendanceComponent
          ),
        canActivate: [authGuard, terminatedRestrictionGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './modules/time-and-attendance/time-and-attendance-main/time-and-attendance-main.component'
              ).then((m) => m.TimeAndAttendanceMainComponent),
          },
          {
            path: 'employee-attendance',
            loadComponent: () =>
              import(
                './modules/time-and-attendance/employee-attendance/employee-attendance.component'
              ).then((m) => m.EmployeeAttendanceComponent),
          },
          {
            path: 'view-employee-attendance-details',
            loadComponent: () =>
              import(
                './modules/time-and-attendance/view-employee-attendance-details/view-employee-attendance-details.component'
              ).then((m) => m.ViewEmployeeAttendanceDetailsComponent),
          },
          {
            path: 'attendance-requests',
            loadComponent: () =>
              import(
                './modules/time-and-attendance/attendance-requests/attendance-requests.component'
              ).then((m) => m.AttendanceRequestsComponent),
          },
          {
            path: 'shift-requests',
            loadComponent: () =>
              import(
                './modules/time-and-attendance/shift-requests/shift-requests.component'
              ).then((m) => m.ShiftRequestsComponent),
          },
        ],
      },
      {
        path: 'attendance-records',
        loadComponent: () =>
          import('./modules/attendence-records/attendence-records.component').then(
            (m) => m.AttendenceRecordsComponent
          ),
        canActivate: [terminatedRestrictionGuard],
      },
      {
        path: 'swipe-clock',
        loadComponent: () =>
          import('./modules/swipe-clock/swipe-clock.component').then(
            (m) => m.SwipeClockComponent
          ),
        canActivate: [terminatedRestrictionGuard],
      },
      {
        path: 'benefits',
        loadComponent: () =>
          import('./modules/benefits/benefits.component').then(
            (m) => m.BenefitsComponent
          ),
        loadChildren: () =>
          import('./modules/benefits/benefits.routes').then(
            (m) => m.BENEFITS_ROUTES
          ),
        canActivate: [authGuard, submoduleGuard],
        data: {
          rights: ['Benefits'],
          redirectTo: '',
        },
      },
      {
        // EN-29808 — Employee Onboarding / Submit New Hire Packet. Authenticated-only; unlike the
        // other tabs above it isn't gated by submoduleGuard/rights since onboarding isn't an
        // optional module an admin can toggle off. Nested here (not top-level) so it renders
        // inside SummaryMainComponent's shell like every other module — side menu included.
        path: 'employee-onboarding',
        loadComponent: () =>
          import('./modules/employee-onboarding/employee-onboarding.component').then(
            (m) => m.EmployeeOnboardingComponent
          ),
        loadChildren: () =>
          import('./modules/employee-onboarding/employee-onboarding.routes').then(
            (m) => m.EMPLOYEE_ONBOARDING_ROUTES
          ),
        canActivate: [authGuard, terminatedRestrictionGuard],
      },
      {
        // EN-18175 - a terminated employee opening a module outside their restricted view lands here.
        path: 'not-available',
        loadComponent: () =>
          import('./core/components/not-available/not-available.component').then(
            (m) => m.NotAvailableComponent
          ),
        canActivate: [authGuard],
      },
    ],
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./core/components/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
  {
    path: 'paycheck-calculator',
    loadComponent: () =>
      import('./modules/paycheck-calculator/paycheck-calculator.component').then(
        (m) => m.PaycheckCalculatorComponent
      ),
  },
];

